import { DEFAULT_SEARCH_PER_PAGE_VALUE } from "@/shared/constants";
import { StoreCatalogSortBy, StoreDirectorySortBy } from "@/shared/store-directory.constants";

import { axios } from "@/utils/axios";
import { hostForLocale } from "@/utils/locale";
import logger from "@/utils/logger";
import { throwOrReturnDefault } from "@/utils/requests";
import { transformImageLocationUrl } from "@/utils/strings";

import { APICategoryData, APICategoryProps, CategoryProps } from "@/interfaces/category-interface";
import { APIProductProps, ProductProps } from "@/interfaces/products-interface";
import { StoreProps, StoresWithCountProps, transformStores } from "@/interfaces/stores-interface";

import { Locale } from "@/types/locale";

const SHOP_DIRECTORY_API_HOST = process.env.NEXT_PUBLIC_SHOP_DIRECTORY_API_HOST;
const SHOP_CATEGORY_STORE_PAGE_SIZE = 1000;
const SHOP_CATEGORY_STORES_TIMEOUT_MS = 30_000;
const SHOP_CATEGORY_NAVIGATION_CACHE_TTL_MS = 5 * 60 * 1000;
const SHOP_CATEGORY_NAVIGATION_FETCH_TIMEOUT_MS = 5_000;
const MAX_SHOP_CATEGORY_STORE_PAGES = 100;

type ShopCategoryNavigationCategories = Array<CategoryProps> & {
    isPartial?: boolean;
};

type ShopCategoryNavigationCacheEntry = {
    expiresAt: number;
    hasResolved: boolean;
    isPartial: boolean;
    promise: Promise<ShopCategoryNavigationCategories>;
};

type ShopCategoryNavigationBypassRefreshState = {
    previousCacheEntry: ShopCategoryNavigationCacheEntry;
    promise: Promise<ShopCategoryNavigationCategories>;
};

type ShopCategoryNavigationResult = {
    categories: Array<CategoryProps>;
    isPartial: boolean;
};

type FetchShopCategoryNavigationOptions = {
    bypassCache?: boolean;
};

type FetchAllCategoryStoresOptions = {
    signal?: AbortSignal;
    sortBy?: StoreCatalogSortBy;
};

const shopCategoryNavigationCache = new Map<Locale, ShopCategoryNavigationCacheEntry>();
const shopCategoryNavigationBypassRefreshStates = new Map<
    Locale,
    ShopCategoryNavigationBypassRefreshState
>();

const isAxiosErrorLoggedByInterceptor = (err: unknown): boolean =>
    Boolean(err && typeof err === "object" && "config" in err);

const getStoreDirectoryApiBaseUrl = (): string => `${SHOP_DIRECTORY_API_HOST}/`;

const createStoreDirectoryApiUrl = (
    pathSegments: Array<string>,
    queryParams?: Record<string, string> | URLSearchParams
): string => {
    const url = new URL(
        pathSegments.map((pathSegment) => encodeURIComponent(pathSegment)).join("/"),
        getStoreDirectoryApiBaseUrl()
    );
    const searchParams =
        queryParams instanceof URLSearchParams
            ? queryParams
            : new URLSearchParams(queryParams ?? {});

    searchParams.forEach((value, key) => {
        url.searchParams.set(key, value);
    });

    return url.toString();
};

const applyCategoryStoreQueryParams = (url: string, queryParams: URLSearchParams): string => {
    const nextUrl = new URL(url, getStoreDirectoryApiBaseUrl());
    const safeNextUrl = new URL(
        nextUrl.pathname.replace(/^\/+/, ""),
        getStoreDirectoryApiBaseUrl()
    );

    nextUrl.searchParams.forEach((value, key) => {
        safeNextUrl.searchParams.set(key, value);
    });

    queryParams.forEach((value, key) => {
        safeNextUrl.searchParams.set(key, value);
    });

    return safeNextUrl.toString();
};

const createAbortError = (): Error => {
    const error = new Error("The operation was aborted");
    error.name = "AbortError";

    return error;
};

const isAbortError = (err: unknown): boolean =>
    err instanceof Error &&
    (err.name === "AbortError" ||
        err.name === "CanceledError" ||
        (err as { code?: string }).code === "ERR_CANCELED");

const isAbortSignal = (
    optionsOrSignal: AbortSignal | FetchAllCategoryStoresOptions | undefined
): optionsOrSignal is AbortSignal =>
    Boolean(
        optionsOrSignal && "aborted" in optionsOrSignal && "addEventListener" in optionsOrSignal
    );

const withAbortSignal = <T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> => {
    if (!signal) {
        return promise;
    }

    if (signal.aborted) {
        return Promise.reject(createAbortError());
    }

    return new Promise((resolve, reject) => {
        const handleAbort = () => reject(createAbortError());

        signal.addEventListener("abort", handleAbort, { once: true });
        void promise.then(resolve, reject).finally(() => {
            signal.removeEventListener("abort", handleAbort);
        });
    });
};

const evictShopCategoryNavigationCachePromise = (
    locale: Locale,
    promise: Promise<ShopCategoryNavigationCategories>
) => {
    if (shopCategoryNavigationCache.get(locale)?.promise === promise) {
        shopCategoryNavigationCache.delete(locale);
    }
};

export const transformProducts = (products: Array<APIProductProps>): Array<ProductProps> => {
    if (!products) {
        return [];
    }
    return products.map(transformProduct);
};

export const transformProduct = (product: APIProductProps): ProductProps => {
    return {
        id: product.attributes.id,
        name: product.attributes.name,
        linkToMerchant: product.attributes.link_to_merchant,
        priceFormatted: product.attributes.price_formatted,
        originalPriceFormatted: product.attributes.original_price_formatted,
        inStock: product.attributes.in_stock,
        storeId: product.attributes.store_id,
        mainImageUrl: `${transformImageLocationUrl(product.attributes.main_image_url)}`,
        merchantId: product.attributes.merchant_id,
        merchantName: product.attributes.merchant_name,
        newMerchantLogoUrl: `${transformImageLocationUrl(
            product.attributes.new_merchant_logo_url
        )}`,
        merchantLogoUrl: `${transformImageLocationUrl(product.attributes.merchant_logo_url)}`,
        affiliate_network: product.attributes.affiliate_network,
    };
};

export const transformCategory = (category: APICategoryProps): CategoryProps => {
    const pageTitle = category.attributes.page_title;

    return {
        description: category.attributes.page_description ?? "",
        id: category.id,
        isStoreCollection: Boolean(category.attributes.is_store_collection),
        metaDescription: category.attributes.meta_description ?? "",
        metaTitle: category.attributes.meta_title ?? "",
        name: category.attributes.name,
        parentId: category.attributes.parent_id?.toString() || null,
        ...(pageTitle ? { pageTitle } : {}),
        position: category.attributes.position,
        rootPageUrl: `/categories/${category.attributes.slug}`,
        slug: category.attributes.slug,
    };
};

export const transformCategories = (categories: Array<APICategoryProps>): Array<CategoryProps> => {
    if (!categories) {
        return [];
    }
    return categories.map(transformCategory);
};

const mergeCategoryResources = (
    categories: Array<APICategoryProps> = [],
    includedCategories: Array<APICategoryProps> = []
): Array<APICategoryProps> => {
    const categoriesById = new Map<string, APICategoryProps>();
    const categoryResources = [...categories, ...includedCategories];

    categoryResources.forEach((category) => {
        if (!category?.id) {
            throw new Error("category response includes invalid category resources");
        }

        categoriesById.set(category.id, category);
    });

    return [...categoriesById.values()];
};

const getUniqueCategories = (categories: Array<APICategoryProps>): Array<APICategoryProps> =>
    mergeCategoryResources(categories);

export const transformCategorySearchCategories = ({
    data = [],
    included = [],
}: APICategoryData): Array<CategoryProps> =>
    transformCategories(mergeCategoryResources(data, included).filter(hasCategorySlug));

const getCategorySlugs = (categories: Array<APICategoryProps>): Array<string> =>
    categories.map((category) => category.attributes.slug).filter((slug) => slug?.length > 0);

export type CategorySearchCategoriesResult = {
    categories: Array<CategoryProps>;
};

type CategorySearchFetchOptions = {
    signal?: AbortSignal;
};

type CategorySearchAPIData = APICategoryData & {
    included: Array<APICategoryProps>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const isCategoryResource = (resource: unknown): resource is APICategoryProps => {
    if (!isRecord(resource) || typeof resource.id !== "string") {
        return false;
    }

    const { attributes } = resource;

    if (!isRecord(attributes)) {
        return false;
    }

    return typeof attributes.name === "string" && typeof attributes.slug === "string";
};

const hasCategorySlug = (category: APICategoryProps): boolean =>
    category.attributes.slug.trim().length > 0;

const getCategoryResources = (resources: unknown): Array<APICategoryProps> | null => {
    if (!Array.isArray(resources) || !resources.every(isCategoryResource)) {
        return null;
    }

    return resources;
};

const getOptionalCategoryResources = (resources: unknown): Array<APICategoryProps> | null => {
    if (resources === undefined) {
        return [];
    }

    return getCategoryResources(resources);
};

const getCategorySearchAPIData = (categoryData: unknown): CategorySearchAPIData | null => {
    if (!categoryData || typeof categoryData !== "object") {
        return null;
    }

    const { data, included } = categoryData as Partial<APICategoryData>;
    const categoryResources = getCategoryResources(data);
    const includedResources = getOptionalCategoryResources(included);

    if (
        !categoryResources ||
        !includedResources ||
        categoryResources.length + includedResources.length === 0
    ) {
        return null;
    }

    return {
        data: categoryResources,
        included: includedResources,
    };
};

const fetchChildCategorySlugs = async (
    locale: Locale,
    subCategorySlugs: Array<string>
): Promise<Array<string>> => {
    const childCategorySlugs: Array<string> = [];

    for (const slug of subCategorySlugs) {
        const url = createStoreDirectoryApiUrl(["api", "v1", "categories", slug], { locale });
        const response = await axios(url);
        const { included = [] } = response.data;

        childCategorySlugs.push(...getCategorySlugs(included));
    }

    return childCategorySlugs;
};

export const fetchCategory = async (
    locale: Locale,
    slug: string | undefined
): Promise<CategoryProps> => {
    if (!slug) {
        return null;
    }

    const url = createStoreDirectoryApiUrl(["api", "v1", "categories", slug], { locale });

    try {
        const categoryResponse = await axios(url, {
            validateStatus: (status) => status === 400 || (status >= 200 && status < 300),
        });

        if (categoryResponse.status === 400) {
            return null;
        }

        const { data } = categoryResponse.data;
        return data ? transformCategory(data) : null;
    } catch (ex) {
        if (ex.response?.status === 400) {
            return null;
        }

        const errorDescription = `failed to fetch category to url=${url}`;
        if (ex.response?.status === 404) {
            return null;
        } else if (!isAxiosErrorLoggedByInterceptor(ex)) {
            logger.logException(ex, errorDescription);
        }
        return throwOrReturnDefault(ex);
    }
};

export const fetchCategoryStores = async (
    locale: Locale,
    slug: string | undefined
): Promise<Array<StoreProps>> => {
    if (!slug) {
        return [];
    }

    const url = createStoreDirectoryApiUrl(["api", "v1", "categories", slug, "stores"], {
        locale,
    });
    try {
        const categoryResponse = await axios(url);
        const { data } = categoryResponse.data;
        return transformStores(data);
    } catch (err) {
        const errorDescription = `failed to fetch category stores url=${url}`;
        if (err.response?.status === 404) {
            logger.logWarn(err, errorDescription);
        } else {
            logger.logException(err, errorDescription);
        }
        return [];
    }
};

export const fetchAllCategoryStores = async (
    locale: Locale,
    slug: string | undefined,
    optionsOrSignal?: AbortSignal | FetchAllCategoryStoresOptions
): Promise<Array<StoreProps>> => {
    if (!slug) {
        return [];
    }

    let signal: AbortSignal | undefined;
    let sortBy: StoreCatalogSortBy = StoreDirectorySortBy.MostPopular;

    if (isAbortSignal(optionsOrSignal)) {
        signal = optionsOrSignal;
    } else {
        signal = optionsOrSignal?.signal;
        sortBy = optionsOrSignal?.sortBy ?? sortBy;
    }

    const queryParams = new URLSearchParams({
        locale,
        per_page: String(SHOP_CATEGORY_STORE_PAGE_SIZE),
        sort_by: sortBy,
    });
    const deadline = Date.now() + SHOP_CATEGORY_STORES_TIMEOUT_MS;
    const visitedUrls = new Set<string>();
    let next: string | null = createStoreDirectoryApiUrl(
        ["api", "v1", "categories", slug, "stores"],
        queryParams
    );
    const stores: Array<StoreProps> = [];

    try {
        while (next) {
            if (visitedUrls.has(next) || visitedUrls.size >= MAX_SHOP_CATEGORY_STORE_PAGES) {
                throw new Error(
                    `failed to fetch all category stores cyclic pagination next=${next}`
                );
            }

            visitedUrls.add(next);
            const remainingTimeout = deadline - Date.now();

            if (remainingTimeout <= 0) {
                throw new Error(`timed out fetching all category stores next=${next}`);
            }

            const url = next;
            const categoryResponse = await axios(url, { signal, timeout: remainingTimeout });
            const { data = [], links } = categoryResponse.data;

            stores.push(...transformStores(data));
            next = links?.next ? applyCategoryStoreQueryParams(links.next, queryParams) : null;
        }

        return stores;
    } catch (err) {
        const errorDescription = `failed to fetch all category stores url=${next}`;
        if (signal?.aborted) {
            throw err;
        }

        if (!isAxiosErrorLoggedByInterceptor(err)) {
            logger.logException(err, errorDescription);
        }
        throw err;
    }
};

export const fetchStoresSearch = async (
    locale: Locale,
    searchQuery: string
): Promise<StoresWithCountProps> => {
    const host = hostForLocale(locale);
    const url = `${host}/api/stores/search?${searchQuery}&per_page=${DEFAULT_SEARCH_PER_PAGE_VALUE}&sort_by=${StoreDirectorySortBy.Relevance}`;

    try {
        const response = await axios.get<StoresWithCountProps>(url);
        return response.data;
    } catch (err) {
        logger.logException(err, `failed to search stores url=${url}`);
        return { totalCount: 0, data: [] };
    }
};

export const fetchCategoryProducts = async (
    locale: Locale,
    slug: string
): Promise<Array<ProductProps>> => {
    const url = createStoreDirectoryApiUrl(["api", "v1", "categories", slug, "products"], {
        locale,
    });

    try {
        const categoryResponse = await axios(url);
        const { data } = categoryResponse.data;
        return transformProducts(data);
    } catch (err) {
        logger.logException(err, `failed to fetch category products url=${url}`);
        return [];
    }
};

const getCategoriesEndpoint = (locale: Locale): string =>
    createStoreDirectoryApiUrl(["api", "v1", "categories"], {
        locale,
        per_page: "1000",
    });

export const fetchCategoriesOrThrow = async (locale: Locale): Promise<Array<CategoryProps>> => {
    const response = await axios(getCategoriesEndpoint(locale));

    if (response.status === 200) {
        const { data } = response.data;
        return transformCategories(data);
    }

    return response.data;
};

export const fetchCategorySearchCategoriesOrThrow = async (
    locale: Locale,
    options: CategorySearchFetchOptions = {}
): Promise<CategorySearchCategoriesResult> => {
    const response = await axios(getCategoriesEndpoint(locale), { signal: options.signal });

    if (response.status === 200) {
        const categoryData = getCategorySearchAPIData(response.data);

        if (!categoryData) {
            throw new Error("category search response missing category arrays");
        }

        const categories = transformCategorySearchCategories(categoryData);

        if (categories.length === 0) {
            throw new Error("category search response missing routable categories");
        }

        return {
            categories,
        };
    }

    throw new Error(`unexpected category search response status=${response.status}`);
};

export const fetchCategories = async (locale: Locale): Promise<Array<CategoryProps>> => {
    try {
        return await fetchCategoriesOrThrow(locale);
    } catch (err) {
        logger.logException(
            err,
            `failed to fetch categories categoryEndpoint=${getCategoriesEndpoint(locale)}`
        );
        return [];
    }
};

const fetchCategoriesWithIncluded = async (
    locale: Locale,
    {
        signal,
        throwOnError = false,
    }: {
        signal?: AbortSignal;
        throwOnError?: boolean;
    } = {}
): Promise<Array<CategoryProps>> => {
    const categoryEndpoint = createStoreDirectoryApiUrl(["api", "v1", "categories"], {
        app: "true",
        locale,
    });
    try {
        const response = await axios(categoryEndpoint, { signal });
        if (response.status === 200) {
            const { data = [], included = [] } = response.data;

            try {
                return transformCategories(getUniqueCategories([...data, ...included]));
            } catch (err) {
                logger.logException(
                    err,
                    `failed to process categories categoryEndpoint=${categoryEndpoint}`
                );
                throw err;
            }
        }

        if (throwOnError) {
            throw new Error(`failed to fetch categories status=${response.status}`);
        }

        return response.data;
    } catch (err) {
        if (signal?.aborted) {
            throw err;
        }

        if (throwOnError) {
            throw err;
        }

        logger.logException(err, `failed to fetch categories categoryEndpoint=${categoryEndpoint}`);
        return [];
    }
};

const getCategoryPosition = (category: CategoryProps): number =>
    category.position ?? Number.MAX_SAFE_INTEGER;

const getOrderedShopCategories = (categories: Array<CategoryProps>): Array<CategoryProps> => {
    const categoriesById = new Map<string, CategoryProps>(
        categories.map((category) => [category.id, category])
    );
    const originalIndexById = new Map<string, number>(
        categories.map((category, index) => [category.id, index])
    );

    const childrenByParentId = new Map<string, Array<CategoryProps>>();
    const rootCategories: Array<CategoryProps> = [];

    categories.forEach((category) => {
        if (category.parentId && categoriesById.has(category.parentId)) {
            const children = childrenByParentId.get(category.parentId) ?? [];
            children.push(category);
            childrenByParentId.set(category.parentId, children);
            return;
        }

        rootCategories.push(category);
    });

    const sortCategories = (items: Array<CategoryProps>) =>
        [...items].sort((firstCategory, secondCategory) => {
            const positionDelta =
                getCategoryPosition(firstCategory) - getCategoryPosition(secondCategory);

            if (positionDelta !== 0) {
                return positionDelta;
            }

            return (
                (originalIndexById.get(firstCategory.id) ?? 0) -
                (originalIndexById.get(secondCategory.id) ?? 0)
            );
        });

    const orderedCategories: Array<CategoryProps> = [];
    const appendedCategoryIds = new Set<string>();

    const appendCategory = (category: CategoryProps) => {
        if (appendedCategoryIds.has(category.id)) {
            return;
        }

        appendedCategoryIds.add(category.id);
        orderedCategories.push(category);
        sortCategories(childrenByParentId.get(category.id) ?? []).forEach(appendCategory);
    };

    sortCategories(rootCategories).forEach(appendCategory);
    sortCategories(categories).forEach(appendCategory);

    return orderedCategories;
};

const fetchShopCategoryNavigationFromApi = async (
    locale: Locale,
    signal?: AbortSignal
): Promise<ShopCategoryNavigationResult> => {
    const categories = await fetchCategoriesWithIncluded(locale, { signal, throwOnError: true });

    if (categories.length === 0) {
        const error = new Error("failed to fetch shop category navigation data");

        logger.logException(
            error,
            `failed to fetch shop category navigation data locale=${locale}`
        );
        throw error;
    }

    return {
        categories: getOrderedShopCategories(categories),
        isPartial: false,
    };
};

export const fetchShopCategoryNavigation = async (
    locale: Locale,
    signal?: AbortSignal,
    options: FetchShopCategoryNavigationOptions = {}
): Promise<ShopCategoryNavigationCategories> => {
    if (signal?.aborted) {
        throw createAbortError();
    }

    const cached = shopCategoryNavigationCache.get(locale);
    const now = Date.now();
    const settledCacheEntry = cached?.hasResolved ? cached : undefined;
    const validCacheEntry = cached && cached.expiresAt > now ? cached : undefined;
    const refreshBaseCacheEntry = settledCacheEntry;

    if (!options.bypassCache && validCacheEntry) {
        return withAbortSignal(validCacheEntry.promise, signal);
    }

    if (options.bypassCache && validCacheEntry && !validCacheEntry.hasResolved) {
        return withAbortSignal(validCacheEntry.promise, signal);
    }

    const activeBypassRefresh = shopCategoryNavigationBypassRefreshStates.get(locale);

    if (
        refreshBaseCacheEntry &&
        activeBypassRefresh?.previousCacheEntry === refreshBaseCacheEntry
    ) {
        return withAbortSignal(activeBypassRefresh.promise, signal);
    }

    const shouldPublishPendingCache = !refreshBaseCacheEntry;
    let didTimeout = false;
    let didLogTimeout = false;
    let shouldDeleteCacheOnSettle = false;
    const logTimeout = () => {
        if (didLogTimeout) {
            return;
        }

        didLogTimeout = true;
        logger.logException(
            new Error("Timed out fetching shop category navigation"),
            `Timed out fetching shop category navigation locale=${locale}`
        );
    };
    const cacheEntry: ShopCategoryNavigationCacheEntry = {
        expiresAt: now + SHOP_CATEGORY_NAVIGATION_CACHE_TTL_MS,
        hasResolved: false,
        isPartial: false,
        promise: Promise.resolve([] as ShopCategoryNavigationCategories),
    };
    const abortController =
        typeof AbortController !== "undefined" ? new AbortController() : undefined;
    const timeout = abortController
        ? setTimeout(() => {
              didTimeout = true;
              abortController.abort();
          }, SHOP_CATEGORY_NAVIGATION_FETCH_TIMEOUT_MS)
        : undefined;
    const promiseHolder: { current?: Promise<ShopCategoryNavigationCategories> } = {};
    const promise = fetchShopCategoryNavigationFromApi(locale, abortController?.signal)
        .then((result) => {
            const categories = Object.assign(result.categories, {
                isPartial: result.isPartial,
            });

            cacheEntry.isPartial = result.isPartial;
            cacheEntry.expiresAt = Date.now() + SHOP_CATEGORY_NAVIGATION_CACHE_TTL_MS;
            cacheEntry.hasResolved = true;

            if (!shouldPublishPendingCache) {
                const currentCacheEntry = shopCategoryNavigationCache.get(locale);

                if (!currentCacheEntry || currentCacheEntry === refreshBaseCacheEntry) {
                    shopCategoryNavigationCache.set(locale, cacheEntry);
                }
            }

            return categories;
        })
        .catch((err) => {
            shouldDeleteCacheOnSettle = true;
            const currentCacheEntry = shopCategoryNavigationCache.get(locale);

            if (
                refreshBaseCacheEntry &&
                (!currentCacheEntry ||
                    currentCacheEntry === cacheEntry ||
                    currentCacheEntry === refreshBaseCacheEntry)
            ) {
                refreshBaseCacheEntry.expiresAt =
                    Date.now() + SHOP_CATEGORY_NAVIGATION_CACHE_TTL_MS;
                shopCategoryNavigationCache.set(locale, refreshBaseCacheEntry);
            }

            if (didTimeout && isAbortError(err)) {
                const timeoutError = new Error("Timed out fetching shop category navigation");

                logTimeout();
                if (refreshBaseCacheEntry) {
                    return refreshBaseCacheEntry.promise;
                }

                throw timeoutError;
            }

            if (refreshBaseCacheEntry) {
                return refreshBaseCacheEntry.promise;
            }

            throw err;
        })
        .finally(() => {
            if (timeout !== undefined) {
                clearTimeout(timeout);
            }

            if (shouldDeleteCacheOnSettle && promiseHolder.current) {
                evictShopCategoryNavigationCachePromise(locale, promiseHolder.current);
            }

            if (!shouldPublishPendingCache && promiseHolder.current) {
                const activeBypassRefresh = shopCategoryNavigationBypassRefreshStates.get(locale);

                if (activeBypassRefresh?.promise === promiseHolder.current) {
                    shopCategoryNavigationBypassRefreshStates.delete(locale);
                }
            }
        });
    promiseHolder.current = promise;
    cacheEntry.promise = promise;

    if (shouldPublishPendingCache) {
        shopCategoryNavigationCache.set(locale, cacheEntry);
    } else if (refreshBaseCacheEntry) {
        shopCategoryNavigationBypassRefreshStates.set(locale, {
            previousCacheEntry: refreshBaseCacheEntry,
            promise,
        });
    }

    return withAbortSignal(promise, signal);
};

export const getParentCategory = async (
    locale: Locale,
    parentId: string
): Promise<CategoryProps | null> => {
    let result = null;
    if (parentId) {
        const categories = await fetchCategories(locale);
        const parentCategory = categories.find((category) => category.id === parentId);
        if (parentCategory) {
            result = parentCategory;
        }
    }

    return result;
};

export const fetchAllCategorySlugs = async (locale: Locale): Promise<Array<string>> => {
    const url = createStoreDirectoryApiUrl(["api", "v1", "categories"], { locale });
    try {
        const response = await axios(url);
        const { data, included = [] } = response.data;

        const topLevelCategorySlugs = getCategorySlugs(data);
        const subCategorySlugs = getCategorySlugs(included);

        const childCategorySlugs = await fetchChildCategorySlugs(locale, subCategorySlugs);

        const uniqueCategorySlugs = new Set([
            ...topLevelCategorySlugs,
            ...subCategorySlugs,
            ...childCategorySlugs,
        ]);

        return [...uniqueCategorySlugs];
    } catch (ex) {
        logger.logException(ex, `failed to fetch all category slugs url=${url}`);
        throw ex;
    }
};
